import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ScrollToTop from '../../components/feature/ScrollToTop';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { useSEO } from '../../utils/seo';

export default function ContactoPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hormipremium.com';

  useSEO({
    title: 'Contacto HORMI PREMIUM Estepona | Hormigón Decorativo',
    description: 'Contacta con HORMI PREMIUM para tu proyecto de hormigón decorativo en Estepona. Teléfono, email, redes sociales y formulario de contacto. Presupuesto gratis.',
    keywords: 'contacto hormigón decorativo, HORMI PREMIUM Estepona, presupuesto hormigón, contacto pavimentos',
    canonical: `${siteUrl}/contacto`,
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('¡Gracias por tu mensaje! Nos pondremos en contacto contigo pronto.');
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/5f189535e44dba685ee53a6e20264b8f.jpg"
            alt="Contacto HORMI PREMIUM"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
        </div>

        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Contacta con Nosotros
          </h1>
          <p className="text-xl md:text-2xl text-gray-200">
            Estamos aquí para ayudarte con tu proyecto de hormigón decorativo
          </p>
        </div>
      </section>

      {/* Información de Contacto */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {[
              {
                icon: 'ri-phone-line',
                title: 'Teléfono',
                content: '640 877 479',
                link: 'tel:+34640877479',
                color: 'bg-blue-500',
              },
              {
                icon: 'ri-mail-line',
                title: 'Email',
                content: 'info@hormipremium.com',
                link: 'mailto:info@hormipremium.com',
                color: 'bg-teal-500',
              },
              {
                icon: 'ri-whatsapp-line',
                title: 'WhatsApp',
                content: 'Chatea con nosotros',
                link: 'https://wa.me/34640877479',
                color: 'bg-green-500',
              },
              {
                icon: 'ri-map-pin-line',
                title: 'Ubicación',
                content: 'Estepona, Andalucía',
                link: '#',
                color: 'bg-orange-500',
              },
            ].map((item, index) => (
              <a
                key={index}
                href={item.link}
                target={item.link.startsWith('http') ? '_blank' : undefined}
                rel={item.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                className="bg-gray-50 rounded-xl p-8 hover:shadow-xl transition-all text-center group cursor-pointer"
              >
                <div className={`w-16 h-16 ${item.color} rounded-lg flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform`}>
                  <i className={`${item.icon} text-3xl text-white`} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.content}</p>
              </a>
            ))}
          </div>

          {/* Redes Sociales */}
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Síguenos en Redes Sociales</h2>
            <div className="flex justify-center gap-6">
              <a
                href="https://www.tiktok.com/@hormipremium?_r=1&_t=ZN-93QU4VRabv4"
                target="_blank"
                rel="noopener noreferrer"
                className="w-16 h-16 bg-gray-900 hover:bg-gray-800 rounded-full flex items-center justify-center transition-all hover:scale-110 cursor-pointer"
                aria-label="TikTok"
              >
                <i className="ri-tiktok-fill text-3xl text-white" />
              </a>
              <a
                href="https://www.facebook.com/share/17XNqDeyDh/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="w-16 h-16 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center transition-all hover:scale-110 cursor-pointer"
                aria-label="Facebook"
              >
                <i className="ri-facebook-fill text-3xl text-white" />
              </a>
              <a
                href="https://wa.me/34640877479"
                target="_blank"
                rel="noopener noreferrer"
                className="w-16 h-16 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center transition-all hover:scale-110 cursor-pointer"
                aria-label="WhatsApp"
              >
                <i className="ri-whatsapp-fill text-3xl text-white" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Formulario de Contacto */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Envíanos un Mensaje
            </h2>
            <p className="text-xl text-gray-600">
              Completa el formulario y te responderemos lo antes posible
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 md:p-12 shadow-xl">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombre completo *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm"
                  placeholder="Tu nombre"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Teléfono *
                </label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm"
                  placeholder="+34 600 000 000"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm"
                placeholder="tu@email.com"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Asunto *
              </label>
              <input
                type="text"
                required
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm"
                placeholder="¿En qué podemos ayudarte?"
              />
            </div>

            <div className="mb-8">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Mensaje *
              </label>
              <textarea
                required
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={6}
                maxLength={500}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all resize-none text-sm"
                placeholder="Cuéntanos sobre tu proyecto o consulta..."
              />
              <p className="text-sm text-gray-500 mt-2">Máximo 500 caracteres</p>
            </div>

            <button
              type="submit"
              className="w-full px-8 py-4 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
            >
              Enviar Mensaje
            </button>
          </form>
        </div>
      </section>

      {/* Horario */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Horario de Atención</h2>
          <div className="bg-gray-50 rounded-xl p-8">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Lunes a Viernes</h3>
                <p className="text-gray-600">9:00 - 18:00</p>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Sábados</h3>
                <p className="text-gray-600">10:00 - 14:00</p>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-6">
              * Disponibilidad de WhatsApp 24/7 para consultas urgentes
            </p>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </>
  );
}
